import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import PrivateRoute from './components/PrivateRoute';
import MainLayout from './components/layout/MainLayout';
import { authService } from './services/auth';

function RequirePermission({ permission, children }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (!hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function RequireHrAdmin({ children }) {
  const user = useAuthStore((s) => s.user);
  if (user?.role !== 'HR_ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const TwoFactorPage = React.lazy(() => import('./pages/TwoFactorPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const OrganizationsPage = React.lazy(() => import('./pages/OrganizationsPage'));
const OrganizationDetailPage = React.lazy(() => import('./pages/OrganizationDetailPage'));
const EmployeesPage = React.lazy(() => import('./pages/EmployeesPage'));
const AddEmployeePage = React.lazy(() => import('./pages/AddEmployeePage'));
const EmployeeDetailPage = React.lazy(() => import('./pages/EmployeeDetailPage'));
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage'));
const DocumentDetailPage = React.lazy(() => import('./pages/DocumentDetailPage'));
const ApprovalsPage = React.lazy(() => import('./pages/ApprovalsPage'));
const ApprovalDetailPage = React.lazy(() => import('./pages/ApprovalDetailPage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const ActivityLogsPage = React.lazy(() => import('./pages/ActivityLogsPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const RBACPage = React.lazy(() => import('./pages/RBACPage'));
const CompleteProfilePage = React.lazy(() => import('./pages/CompleteProfilePage'));
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const SignI9Page = React.lazy(() => import('./pages/SignI9Page'));
const SignFormPage = React.lazy(() => import('./pages/SignFormPage'));

function LoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        <p className="mt-4 text-sm text-secondary-500">Loading...</p>
      </div>
    </div>
  );
}

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // On every page load, fetch fresh permissions from the backend so that
  // RBAC changes made by an admin are reflected without requiring logout/login.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;
    authService.getMe()
      .then((res) => {
        if (res.data) {
          const { user, setUser } = useAuthStore.getState();
          if (user) setUser({ ...user, ...res.data });
        }
      })
      .catch(() => {
        // silently fail — stale cached permissions remain until next successful fetch
      });
  }, [isInitialized, isAuthenticated]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Profile completion — protected but no sidebar/header */}
        <Route
          path="/complete-profile"
          element={
            <PrivateRoute>
              <CompleteProfilePage />
            </PrivateRoute>
          }
        />

        {/* Onboarding — protected, standalone (no sidebar) */}
        <Route
          path="/onboarding"
          element={
            <PrivateRoute>
              <OnboardingPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/sign-i9"
          element={
            <PrivateRoute>
              <SignI9Page />
            </PrivateRoute>
          }
        />

        <Route
          path="/sign-form"
          element={
            <PrivateRoute>
              <SignFormPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/employees/new"
          element={
            <PrivateRoute>
              <RequirePermission permission="employees.create">
                <AddEmployeePage />
              </RequirePermission>
            </PrivateRoute>
          }
        />

        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<TwoFactorPage />} />
        <Route path="/register/:token" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes */}
        <Route
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="organizations" element={<RequirePermission permission="organizations.view"><OrganizationsPage /></RequirePermission>} />
          <Route path="organizations/:id" element={<RequirePermission permission="organizations.view"><OrganizationDetailPage /></RequirePermission>} />
          <Route path="employees" element={<RequirePermission permission="employees.view"><EmployeesPage /></RequirePermission>} />
          <Route path="employees/:id" element={<RequirePermission permission="employees.view"><EmployeeDetailPage /></RequirePermission>} />
          <Route path="documents" element={<RequirePermission permission="documents.view"><DocumentsPage /></RequirePermission>} />
          <Route path="documents/:id" element={<DocumentDetailPage />} />
          <Route path="approvals" element={<RequirePermission permission="approvals.view"><ApprovalsPage /></RequirePermission>} />
          <Route path="approvals/:id" element={<RequirePermission permission="approvals.view"><ApprovalDetailPage /></RequirePermission>} />
          <Route path="notifications" element={<RequirePermission permission="notifications.view"><NotificationsPage /></RequirePermission>} />
          <Route path="activity-logs" element={<RequirePermission permission="activity_logs.view"><ActivityLogsPage /></RequirePermission>} />
          <Route path="settings" element={<RequirePermission permission="settings.view"><SettingsPage /></RequirePermission>} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="rbac" element={<RequireHrAdmin><RBACPage /></RequireHrAdmin>} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
